import React, { useEffect, useState, useContext } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";
import { Button, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import DropdownMain from "../../../../../components/dropdown-main/DropdownMain";
import {
  AddWorkflow,
  UpdateWorkflow,
  GetWorkflowById,
  GetAllCustomReminders,
  GetBaseWorkflowStatusesLegacy,
} from "../../../../../services/api/workflow-api";
import GeneralProperties from "../../../../../assets/icons/Icon-Window-Bullet-List-Blue-16px.svg";
import DisabledPlus from "../../../../../assets/icons/Icon - Add - Disabled.svg";
import TaskActions from "../../../../../assets/icons/Icon-Flow-Blue-16px.svg";
import { v4 as uuidv4, NIL as NIL_UUID } from "uuid";
import CancelModal from "../../../../../components/cancel-modal/cancel-modal";
import {
  errorNotification,
  successNotification,
} from "../../../../../redux/notifications";
import EditWorkflowStatusLegacy from "./edit-workflow-status-legacy";
import NoPermissionTable from "../../../../../components/no-permission-table/NoPermissionTable";
import WorkflowHeader from "./subcomponents/WorkflowHeader";
import {
  GetCurrentUserNewAPI,
  LoginContext,
} from "../../../../../services/api/authentication-api";

const CreateEditWorkflowLegacy = ({ useLegacyMode, onToggleChange, showLegacyToggle, ...props }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const context = useContext(LoginContext);

  const [editMode] = useState(
    history.location.pathname.split("/")[3] === "edit"
  );
  const [initialStatusList, setInitialStatusList] = useState([]);
  const [workflowBasic, setWorkflowBasic] = useState({});
  const [workflowStatuses, setWorkflowStatuses] = useState([]);
  const [workflowBaseStatuses, setWorkflowBaseStatuses] = useState([]);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState([]);
  const [permission, setPermission] = useState(true);

  const [customReminders, setCustomReminders] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const canEdit = context?.currentUser?.privileges["Workflow"]?.Edit || false;

  useEffect(() => {
    getPermission();
    getCustomReminders();
    if (editMode) {
      getWorkflowById();
    } else {
      initialStatusListLoad([]);
      getBaseWorkflowStatusesLegacy();
    }
  }, []);

  const getPermission = () => {
    GetCurrentUserNewAPI(context?.userID)
      .then((res) => {
        setPermission(res?.User?.Role?.SuperAdmin || res?.User?.Role?.Admin); //REMOVE ADMIN CHECK WHEN ROLLING OUT TO ALL CUSTOMERS
      })
      .catch((err) => {
        console.log("Error [GetUser] => ", err);
        setPermission(false);
      });
  };

  const getCustomReminders = () => {
    GetAllCustomReminders()
      .then((res) => {
        if (res.status === 200) {
          let result = res.data;
          setCustomReminders(result);
        }
      })
      .catch((err) => {
        console.log("Error [GetCustomReminders] => ", err);
      });
  };

  //#region Workflow
  const getWorkflowById = () => {
    GetWorkflowById(history.location.pathname.split("/")[4])
      .then((res) => {
        if (res.status === 200) {
          let result = res.data;
          result.InitialStatus = {
            id: result.InitialStatus,
            name: result.InitialStatus,
          };
          setWorkflowBasic(result);
          setWorkflowStatuses(result?.Statuses?.filter((x) => !x.IsBase));
          setWorkflowBaseStatuses(result?.Statuses?.filter((x) => x.IsBase));
          initialStatusListLoad(
            result?.Statuses?.filter((x) => x.Active && !x.IsBase).map((x) => {
              return { id: x.Name, name: x.Name, isBase: false };
            })
          );
        }
      })
      .catch((err) => {
        console.log("Error [GetWorkflowById] => ", err);
      });
  };

  const getBaseWorkflowStatusesLegacy = () => {
    GetBaseWorkflowStatusesLegacy()
      .then((res) => {
        if (res.status === 200) {
          let result = res.data;
          let initStat = result
            ?.filter((x) => x.IsInitialStatus)
            .map((x) => {
              return { id: x.Name, name: x.Name, isBase: x.IsBase };
            });
          if (initStat?.length > 0) {
            setWorkflowBasic({ ...workflowBasic, InitialStatus: initStat[0] });
          }
          setWorkflowBaseStatuses(res.data);
        }
      })
      .catch((err) => {
        console.log("Error [GetBaseWorkflowStatusesLegacy] => ", err);
      });
  };

  const initialStatusListLoad = (statuses) => {
    let initialStatusList = [
      { id: "Not Started", name: "Not Started", isBase: true },
      { id: "In Progress", name: "In Progress", isBase: true },
    ];
    initialStatusList = initialStatusList?.concat(statuses);
    setInitialStatusList(initialStatusList);
  };

  const setFieldName = (id, name) => {
    setWorkflowStatuses(
      workflowStatuses.map((field) => {
        if (field.Id === id) return { ...field, Name: name };
        return field;
      })
    );
  };

  const hideBaseStatus = (id, hidden) => {
    setWorkflowBaseStatuses(
      workflowBaseStatuses.map((field) => {
        if (field.Id === id) return { ...field, IsHidden: !hidden };
        return field;
      })
    );
  };

  const setAutomaticTriggerForInitialBaseStatus = (name) => {
    setWorkflowBaseStatuses(
      workflowBaseStatuses.map((field) => {
        if (field.Name === name) return { ...field, IsInitialStatus: true };
        else
          return {
            ...field,
            IsInitialStatus: false,
            AutomaticTriggers: field?.AutomaticTriggers?.split(",")
              .filter((trigger) => trigger != "Task Creation")
              .join(","),
          };
      })
    );
    setWorkflowStatuses(
      workflowStatuses.map((field) => {
        return {
          ...field,
          IsInitialStatus: false,
          AutomaticTriggers: field?.AutomaticTriggers?.split(",")
            .filter((trigger) => trigger != "Task Creation")
            .join(","),
        };
      })
    );
  };

  const setAutomaticTriggerForInitialStatus = (name) => {
    setWorkflowStatuses(
      workflowStatuses.map((field) => {
        if (field.Name === name) return { ...field, IsInitialStatus: true };
        else
          return {
            ...field,
            IsInitialStatus: false,
            AutomaticTriggers: field?.AutomaticTriggers?.split(",")
              .filter((trigger) => trigger != "Task Creation")
              .join(","),
          };
      })
    );
    setWorkflowBaseStatuses(
      workflowBaseStatuses.map((field) => {
        return {
          ...field,
          IsInitialStatus: false,
          AutomaticTriggers: field?.AutomaticTriggers?.split(",")
            .filter((trigger) => trigger != "Task Creation")
            .join(","),
        };
      })
    );
  };

  const removeField = (removeIdx) => {
    setWorkflowStatuses(workflowStatuses.filter((_, idx) => removeIdx !== idx));
  };

  const removeInitialStatus = (name) => {
    setInitialStatusList(initialStatusList.filter((x) => name !== x.name));
  };

  const addNewWorkflowStatus = () => {
    let obj = {
      Id: uuidv4(),
      Name:
        workflowStatuses?.length == 0
          ? "New Task Action"
          : "New Task Action - " + workflowStatuses?.length,
      Statuses: [],
      MaxInstances: 0,
      AvailableForStatus: NIL_UUID,
      HideForStatus: NIL_UUID,
      Active: true,
      DueDateExtensionDefault: 0,
      DueDateExtendProject: null,
      ExtendByDaysMax: 0,
    };
    setWorkflowStatuses([...workflowStatuses, obj]);
    setInitialStatusList([
      ...initialStatusList,
      { id: obj.Name, name: obj.Name, isBase: false },
    ]);
  };

  const handleSwap = (firstIdx, secondIdx) => {
    //Animation For row swapping, Id's are unique by appending FieldId + Column ID
    let a = document.getElementById(`workflowstatus_` + firstIdx);
    let b = document.getElementById(`workflowstatus_` + secondIdx);
    //remove existing classes
    a.classList.remove("moveUp");
    a.classList.remove("moveDown");
    b.classList.remove("moveUp");
    b.classList.remove("moveDown");
    //trigger reflow
    void a.offsetWidth;
    void b.offsetWidth;
    //add animation
    a.classList.add("moveDown");
    b.classList.add("moveUp");

    const [first, second] = [
      workflowStatuses[firstIdx],
      workflowStatuses[secondIdx],
    ];

    setWorkflowStatuses(
      workflowStatuses.map((field, idx) => {
        if (idx === firstIdx) return second;
        if (idx === secondIdx) return first;
        return field;
      })
    );
  };

  const saveWorkflow = () => {
    if (!editMode) {
      //Create
      let workflowDto = {
        Id: uuidv4(),
        Name: workflowBasic?.Name,
        Legacy: true,
        Description: workflowBasic?.Description,
        InitialStatus: workflowBasic?.InitialStatus?.name,
        Statuses: workflowBaseStatuses?.concat(workflowStatuses),
      };
      if (workflowBasic?.Name == null || workflowBasic?.Name == "") {
        dispatch(
          errorNotification({
            header: "Workflow Creation Failed",
            message: "Please Enter Workflow Name.",
          })
        );
      } else if (
        workflowBasic?.InitialStatus == null ||
        workflowBasic?.InitialStatus == ""
      ) {
        dispatch(
          errorNotification({
            header: "Workflow Creation Failed",
            message: "Please Select Initial Status.",
          })
        );
      } else {
        AddWorkflow(workflowDto)
          .then((res) => {
            if (res.status === 200) {
              history.push("/admin?tab=workflows");
              dispatch(
                successNotification({
                  header: "Workflow Created",
                  message: `${workflowBasic?.Name} Created Successfully.`,
                })
              );
            }
          })
          .catch((err) => {
            console.log(
              "Cannot Save Workflow Settings : CreateWorkflow : ",
              err
            );
            dispatch(
              errorNotification({
                header: "Workflow Creation Failed",
                message: err?.response?.data?.message,
              })
            );
          });
      }
    } else {
      //Edit
      let workflowDto = {
        Id: workflowBasic.Id,
        Name: workflowBasic.Name,
        Legacy: true,
        Description: workflowBasic.Description,
        InitialStatus: workflowBasic.InitialStatus.name,
        Statuses: workflowBaseStatuses?.concat(workflowStatuses),
      };
      UpdateWorkflow(workflowDto)
        .then((res) => {
          if (res.status === 200) {
            history.push("/admin?tab=workflows");
            dispatch(
              successNotification({
                header: "Workflow Updated",
                message: `${workflowBasic?.Name} Updated Successfully.`,
              })
            );
          } else {
            dispatch(
              errorNotification({
                header: "Workflow Update Failed",
                message: "Unable to update workflow. Error Code :" + res.status,
              })
            );
          }
        })
        .catch((err) => {
          console.log("Cannot Save Workflow Settings : Update : ", err);
          dispatch(
            errorNotification({
              header: "Workflow Creation Failed",
              message: err?.response?.data?.message,
            })
          );
        });
    }
  };
  //#endregion

  //#region Workflow Status
  if (!permission) {
    return <NoPermissionTable />;
  } else {
    return (
      <div className="admin-workflow-page-wrapper">
        <Helmet>
          <meta charSet="utf-8" />
          <title>
            {editMode
              ? "TRIYO · " + workflowBasic?.Name
              : "TRIYO · Create Workflow"}
          </title>
        </Helmet>
        <WorkflowHeader 
          editMode={editMode} 
          name={workflowBasic?.Name}
          useLegacyMode={useLegacyMode}
          onToggleChange={onToggleChange}
          showLegacyToggle={showLegacyToggle}
        />
        <div className="workflow-creation-panel">
          {/* GENERAL PROPERTIES */}
          <div className="workflow-section-panel">
            <div className="workflow-section-description">
              <img src={GeneralProperties}></img>
              General Properties
            </div>
            <div className="workflow-section-body">
              <Form>
                <Form.Row className="left-row">
                  <Form.Group className="user-full-name">
                    <Form.Label>
                      Workflow Name <span style={{ color: "red" }}> * </span>
                    </Form.Label>
                    <Form.Control
                      id="userNameFull"
                      name="userNameFull"
                      placeholder="Enter Workflow Name"
                      value={workflowBasic?.Name}
                      onChange={(e) => {
                        setWorkflowBasic({
                          ...workflowBasic,
                          Name: e.target.value,
                        });
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="description">
                    <Form.Label>Workflow Description</Form.Label>
                    <Form.Control
                      id="description"
                      name="description"
                      autoComplete="off"
                      placeholder="Enter workflow description"
                      className="project-textarea textarea"
                      value={workflowBasic?.Description}
                      onChange={(e) => {
                        setWorkflowBasic({
                          ...workflowBasic,
                          Description: e.target.value,
                        });
                      }}
                      as="textarea"
                      rows="3"
                    />
                  </Form.Group>
                  <Form.Label>
                    Start Workflow From{" "}
                    <span style={{ color: "red" }}> * </span>
                  </Form.Label>
                  <DropdownMain
                    values={
                      initialStatusList &&
                      initialStatusList.map((options) => {
                        return options;
                      })
                    }
                    dropdownButtonText={"Select Initial Status"}
                    onSelect={(selectedValue) => {
                      setWorkflowBasic({
                        ...workflowBasic,
                        InitialStatus: selectedValue,
                      });
                      if (selectedValue?.isBase) {
                        setAutomaticTriggerForInitialBaseStatus(
                          selectedValue?.name
                        );
                      } else {
                        setAutomaticTriggerForInitialStatus(
                          selectedValue?.name
                        );
                      }
                    }}
                    currentSelected={workflowBasic?.InitialStatus}
                  />
                </Form.Row>
              </Form>
            </div>
          </div>

          <hr className="divider"></hr>

          {/* TASK ACTIONS */}
          <div className="workflow-section-panel">
            <div className="workflow-section-description">
              <img src={TaskActions}></img>
              Task Actions
            </div>
            <div className="workflow-status-section-body">
              {workflowBaseStatuses
                ?.filter((x) => !x.IsHidden)
                .map((field, fieldIdx) => {
                  return (
                    <div
                      className="individual-workflow"
                      id={"workflowstatusBase_" + fieldIdx}
                      key={field?.Id}
                    >
                      <div className="individual-workflow-status">
                        <span className="status-index">{fieldIdx + 1}</span>
                        <img src={DisabledPlus}></img>
                        <div className="field-container-base-status pl-2">
                          {field?.Name}
                          <OverlayTrigger
                            placement={"top"}
                            delay={{ show: 150, hide: 150 }}
                            overlay={
                              <Tooltip id="button-tooltip">
                                {field.AutomaticTriggers != ""
                                  ? "This Task Action is set to be automatically triggered and can’t be hidden."
                                  : field?.IsInitialStatus
                                  ? "This Task Status has been set as the Workflow starting point. You can’t hide it from the workflow."
                                  : "Click to Hide"}
                              </Tooltip>
                            }
                          >
                            <Button
                              className={`btn-icon1 hide-eye-icon ${
                                field?.AutomaticTriggers != "" ||
                                field?.IsInitialStatus
                                  ? "hide-eye-icon-disabled"
                                  : ""
                              }`}
                              onClick={() => {
                                if (
                                  field?.AutomaticTriggers == "" &&
                                  !field?.IsInitialStatus
                                )
                                  hideBaseStatus(field.Id, field?.IsHidden);
                              }}
                              type="button"
                            />
                          </OverlayTrigger>
                        </div>
                        {(field.Name == "Not Started"
                        || field.Name == "Re-assign Task"
                        || field.Name == "Teams Chat"
                        || field.Name == "In Progress") ? (
                          <Button
                            className="btn-icon1 open-modal"
                            onClick={() =>
                              setSelectedWorkflowStatus({ ...field })
                            }
                            type="button"
                          />
                        ) : (
                          <OverlayTrigger
                            placement={"top"}
                            delay={{ show: 150, hide: 150 }}
                            overlay={
                              <Tooltip id="button-tooltip">
                                TRIYO created this Task Action. You can’t edit
                                its name and properties.
                              </Tooltip>
                            }
                          >
                            <Button
                              className="btn-icon1 lock-closed-icon"
                              type="button"
                            />
                          </OverlayTrigger>
                        )}
                      </div>
                    </div>
                  );
                })}
              { workflowStatuses.map((field, fieldIdx) => {
                const isMoveUpDisabled = fieldIdx === 0;
                const isMoveDownDisabled =
                  fieldIdx === workflowStatuses.length - 1;
                return (
                  <div
                    className="individual-workflow"
                    id={"workflowstatus_" + fieldIdx}
                    key={field?.Id}
                  >
                    <div className="individual-workflow-status">
                      <span className="status-index">
                        {fieldIdx +
                          workflowBaseStatuses?.filter((x) => !x.IsHidden)
                            ?.length +
                          1}
                      </span>
                      <img src={DisabledPlus}></img>
                      <div className="field-container">
                        <div className="row">
                          <div className="col-md-12">
                            {" "}
                            <Form.Control
                              id="userNameFull"
                              name="userNameFull"
                              placeholder="Enter Workflow Name"
                              value={field?.Name}
                              onChange={(e) => {
                                setFieldName(field.Id, e.target.value);
                              }}
                            />
                          </div>
                          <div className="col-md-12 field-description">
                            {field?.Description &&
                            field.Description.trim().length > 0
                              ? field.Description.length > 256
                                ? `${field.Description.substring(0, 256)}...`
                                : field.Description
                              : ""}
                          </div>
                        </div>
                      </div>
                      <Button
                        className="btn-icon1 open-modal"
                        onClick={() => setSelectedWorkflowStatus({ ...field })}
                        type="button"
                      />
                    </div>
                    <div className="field-controls">
                      <Button
                        className={`btn-icon1 rotate-180 arrow${
                          isMoveUpDisabled ? "-disabled" : ""
                        }`}
                        disabled={isMoveUpDisabled}
                        onClick={() => handleSwap(fieldIdx - 1, fieldIdx)}
                        type="button"
                      />
                      <Button
                        className={`btn-icon1 arrow${
                          isMoveDownDisabled ? "-disabled" : ""
                        }`}
                        disabled={isMoveDownDisabled}
                        onClick={() => handleSwap(fieldIdx, fieldIdx + 1)}
                        type="button"
                      />
                      <Button
                        className="btn-icon1 delete"
                        onClick={() => {
                          removeField(fieldIdx);
                          removeInitialStatus(field.Name);
                        }}
                        type="button"
                      />
                    </div>
                  </div>
                );
              })}

              <div className="add-btn-workflow-container mb-3">
                <Button
                  className="add-field-btn-workflow"
                  variant="outline-primary"
                  onClick={addNewWorkflowStatus}
                >
                  <span className="add-field-btn-text">
                    Add New Task Action
                  </span>
                  <div className="add-field-icon" />
                </Button>
              </div>
              { workflowBaseStatuses
                ?.filter((x) => x.IsHidden)
                .map((field, fieldIdx) => {
                  return (
                    <div
                      className="individual-workflow"
                      id={"workflowstatusBase_" + fieldIdx}
                      key={field?.Id}
                    >
                      <div className="individual-workflow-status hidden-base-status">
                        <img src={DisabledPlus}></img>
                        <span className="status-index"></span>
                        <div className="field-container-base pl-2">
                          {field?.Name}
                        </div>
                        <OverlayTrigger
                          placement={"top"}
                          delay={{ show: 150, hide: 150 }}
                          overlay={
                            <Tooltip id="button-tooltip">
                              Click to unhide
                            </Tooltip>
                          }
                        >
                          <Button
                            className={`btn-icon1 unhide-eye-icon`}
                            onClick={() =>
                              hideBaseStatus(field.Id, field?.IsHidden)
                            }
                            type="button"
                          />
                        </OverlayTrigger>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          <hr className="divider"></hr>

          <div className="float-right">
            <Button
              className="btn-wide btn-outlined"
              onClick={() => setShowCancelModal(true)}
            >
              Cancel
            </Button>
            <Button
              className="btn-wide"
              type="submit"
              onClick={() => saveWorkflow()}
               disabled={editMode && !canEdit}
            >
              {editMode ? "Save" : "Create"}
            </Button>
          </div>
        </div>

        {/* Workflow Status Edit Modal */}
        <EditWorkflowStatusLegacy
          isModelOpen={isModelOpen}
          setIsModelOpen={setIsModelOpen}
          openWorkflowStatus={selectedWorkflowStatus}
          workflowStatuses={workflowStatuses}
          setWorkflowStatuses={setWorkflowStatuses}
          workflowBaseStatuses={workflowBaseStatuses}
          setWorkflowBaseStatuses={setWorkflowBaseStatuses}
          customReminders={customReminders}
          workflowBasic={workflowBasic}
          setWorkflowBasic={setWorkflowBasic}
        />
        { showCancelModal && (
          <CancelModal
            show={showCancelModal}
            onHide={() => setShowCancelModal(false)}
            redirectURL={"/admin?tab=workflows"}
          />
        )}
      </div>
    );
  }
};

CreateEditWorkflowLegacy.propTypes = {
  id: PropTypes.number,
  useLegacyMode: PropTypes.bool,
  onToggleChange: PropTypes.func,
  showLegacyToggle: PropTypes.bool,
};

export default CreateEditWorkflowLegacy;
